import { Container, Text, TextStyle, Graphics } from 'pixi.js';
import { KILL_TEXT_DURATION, KILL_TEXT_RISE_SPEED } from '../../constants';

/**
 * Individual text popup data
 */
interface TextPopup {
  /** Container for text and effects */
  container: Container;
  /** Text element */
  text: Text;
  /** Background graphics (optional) */
  background?: Graphics;
  /** Current Y velocity */
  vy: number;
  /** Remaining lifetime in seconds */
  lifetime: number;
  /** Total lifetime for progress calculation */
  maxLifetime: number;
  /** Whether popup is active */
  active: boolean;
}

/**
 * Popup configuration options
 */
export interface PopupConfig {
  /** Text to display */
  text: string;
  /** Position X */
  x: number;
  /** Position Y */
  y: number;
  /** Text color (default: white) */
  color?: number;
  /** Font size (default: 24) */
  fontSize?: number;
  /** Duration in seconds (default: KILL_TEXT_DURATION) */
  duration?: number;
  /** Rise speed (pixels per second, default: KILL_TEXT_RISE_SPEED) */
  riseSpeed?: number;
  /** Scale multiplier for emphasis (default: 1) */
  scale?: number;
  /** Whether to show background (default: false) */
  showBackground?: boolean;
  /** Background color (default: 0x000000) */
  backgroundColor?: number;
}

/**
 * TextPopupSystem - Floating text effects for kills, combos, scores
 *
 * Features:
 * - Rising text animations
 * - Scale and fade effects
 * - Optional backgrounds with glow
 * - Pooled for performance
 */
export class TextPopupSystem {
  /** Main container for popups */
  public container: Container;

  /** Active popups */
  private popups: TextPopup[] = [];

  /** Pool of inactive popups for reuse */
  private popupPool: TextPopup[] = [];

  /** Maximum pooled popups */
  private maxPoolSize: number = 20;

  constructor() {
    this.container = new Container();
    this.container.label = 'text-popup-system';
  }

  /**
   * Get or create a popup from the pool
   */
  private getPopup(): TextPopup {
    // Try to find an inactive popup
    for (const popup of this.popupPool) {
      if (!popup.active) {
        return popup;
      }
    }

    // Create new popup if pool not full
    if (this.popupPool.length < this.maxPoolSize) {
      const container = new Container();
      const text = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: 'Arial, sans-serif',
          fontSize: 24,
          fontWeight: 'bold',
          fill: 0xffffff,
          stroke: { color: 0x000000, width: 4 },
          dropShadow: {
            color: 0x000000,
            blur: 4,
            distance: 2,
            angle: Math.PI / 4,
          },
        }),
      });
      text.anchor.set(0.5, 0.5);
      container.addChild(text);
      container.visible = false;
      this.container.addChild(container);

      const popup: TextPopup = {
        container,
        text,
        vy: 0,
        lifetime: 0,
        maxLifetime: KILL_TEXT_DURATION,
        active: false,
      };
      this.popupPool.push(popup);
      return popup;
    }

    // Recycle oldest active popup
    let oldest: TextPopup | null = null;
    let oldestLifetime = Infinity;
    for (const popup of this.popups) {
      if (popup.lifetime < oldestLifetime) {
        oldestLifetime = popup.lifetime;
        oldest = popup;
      }
    }
    if (oldest) {
      oldest.active = false;
      oldest.container.visible = false;
      const index = this.popups.indexOf(oldest);
      if (index > -1) this.popups.splice(index, 1);
      return oldest;
    }

    // Fallback - just return first pool item
    return this.popupPool[0]!;
  }

  /**
   * Spawn a text popup
   */
  spawn(config: PopupConfig): void {
    const popup = this.getPopup();

    const {
      text,
      x,
      y,
      color = 0xffffff,
      fontSize = 24,
      duration = KILL_TEXT_DURATION,
      riseSpeed = KILL_TEXT_RISE_SPEED,
      scale = 1,
      showBackground = false,
      backgroundColor = 0x000000,
    } = config;

    // Update text style
    popup.text.style.fontSize = fontSize;
    popup.text.style.fill = color;
    popup.text.text = text;

    // Position
    popup.container.position.set(x, y);
    popup.container.scale.set(scale * 1.2); // Start slightly bigger
    popup.container.alpha = 1;
    popup.container.visible = true;

    // Setup animation
    popup.vy = -riseSpeed;
    popup.lifetime = duration;
    popup.maxLifetime = duration;
    popup.active = true;

    // Remove old background if exists
    if (popup.background) {
      popup.container.removeChild(popup.background);
      popup.background.destroy();
      delete popup.background;
    }

    // Add background if requested
    if (showBackground) {
      const bg = new Graphics();
      const padding = 8;
      const width = popup.text.width + padding * 2;
      const height = popup.text.height + padding * 2;
      bg.roundRect(-width / 2, -height / 2, width, height, 6);
      bg.fill({ color: backgroundColor, alpha: 0.7 });
      bg.stroke({ color: color, width: 2, alpha: 0.5 });
      popup.container.addChildAt(bg, 0);
      popup.background = bg;
    }

    this.popups.push(popup);
  }

  /**
   * Spawn a kill notification with arcade-style flair
   */
  spawnKillText(x: number, y: number, victimName: string): void {
    // Random arcade-style kill phrases
    const phrases = ['GOTCHA', 'POW', 'BOOM', 'WHAM', 'SPLAT', 'CHOMP'];
    const phrase = phrases[Math.floor(Math.random() * phrases.length)];

    // Main kill text with exciting colors
    this.spawn({
      text: `${phrase}! ${victimName.toUpperCase()}`,
      x,
      y,
      color: 0xffff00, // Bright yellow for arcade feel
      fontSize: 32,
      scale: 1.3,
      duration: 1.5,
      riseSpeed: 60,
    });
  }

  /**
   * Spawn a score popup (like +100)
   */
  spawnScorePopup(x: number, y: number, amount: number, color: number = 0xffd700): void {
    this.spawn({
      text: `+${amount}`,
      x,
      y,
      color,
      fontSize: 20,
      scale: 1,
      duration: 1.0,
      riseSpeed: 80,
    });
  }

  /**
   * Spawn a combo text with arcade-style names
   */
  spawnComboText(x: number, y: number, comboCount: number): void {
    // Arcade-style combo names with escalating excitement
    const comboNames = [
      'DOUBLE!',       // 2x
      'TRIPLE!',       // 3x
      'MEGA!',         // 4x
      'ULTRA!',        // 5x
      'MONSTER!',      // 6x
      'INSANE!',       // 7x
      'GODLIKE!',      // 8x+
    ];

    // Colors that escalate in intensity (retro arcade palette)
    const colors = [
      0x00ff00, // Green - Double
      0xffff00, // Yellow - Triple
      0xff8800, // Orange - Mega
      0xff4400, // Red-orange - Ultra
      0xff00ff, // Magenta - Monster
      0x00ffff, // Cyan - Insane
      0xff0000, // Pure red - Godlike
    ];

    const nameIndex = Math.min(comboCount - 2, comboNames.length - 1);
    const comboName = comboNames[Math.max(0, nameIndex)] ?? 'COMBO!';
    const color = colors[Math.max(0, nameIndex)] ?? 0xffffff;

    this.spawn({
      text: comboName,
      x,
      y,
      color,
      fontSize: 36 + Math.min(comboCount * 3, 20),
      scale: 1.2 + Math.min(comboCount * 0.15, 0.8),
      duration: 1.8,
      riseSpeed: 50,
    });
  }

  /**
   * Update all popups
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    const toRemove: number[] = [];

    for (let i = this.popups.length - 1; i >= 0; i--) {
      const popup = this.popups[i];
      if (!popup || !popup.active) continue;

      // Update lifetime
      popup.lifetime -= dt;

      if (popup.lifetime <= 0) {
        popup.active = false;
        popup.container.visible = false;
        toRemove.push(i);
        continue;
      }

      // Calculate progress (0 = start, 1 = end)
      const progress = 1 - popup.lifetime / popup.maxLifetime;

      // Rise animation
      popup.container.y += popup.vy * dt;

      // Slow down rise over time
      popup.vy *= 0.98;

      // Scale animation: start big, settle to normal, then shrink at end
      let scale: number;
      if (progress < 0.2) {
        // Initial pop-in: 1.2 -> 1.0
        scale = 1.2 - progress * 1;
      } else if (progress < 0.7) {
        // Hold at normal
        scale = 1.0;
      } else {
        // Shrink out
        scale = 1.0 - (progress - 0.7) * 2;
      }
      popup.container.scale.set(Math.max(0.1, scale));

      // Fade out in last 30%
      if (progress > 0.7) {
        popup.container.alpha = 1 - (progress - 0.7) / 0.3;
      } else {
        popup.container.alpha = 1;
      }
    }

    // Remove dead popups from active list
    for (const index of toRemove) {
      this.popups.splice(index, 1);
    }
  }

  /**
   * Get active popup count
   */
  getActiveCount(): number {
    return this.popups.length;
  }

  /**
   * Clear all popups
   */
  clear(): void {
    for (const popup of this.popups) {
      popup.active = false;
      popup.container.visible = false;
    }
    this.popups.length = 0;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.clear();
    for (const popup of this.popupPool) {
      popup.text.destroy();
      popup.background?.destroy();
      popup.container.destroy({ children: true });
    }
    this.popupPool.length = 0;
    this.container.destroy({ children: true });
  }
}
